import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

export default function SearchSelect({
    value,
    onChange,
    options = [],
    placeholder = "Selecciona...",
    searchPlaceholder = "Buscar...",
    emptyText = "Sin resultados",
    allowClear = true,
    disabled = false,
    searchValue,
    onSearchChange,
    showOptionsWhenEmpty = true,
    footer = null,
    loading = false,
    "data-testid": testid,
}) {
    const [open, setOpen] = useState(false);
    const [internalSearch, setInternalSearch] = useState("");
    const search = searchValue ?? internalSearch;
    const setSearch = onSearchChange ?? setInternalSearch;

    const normalize = (s) => String(s ?? "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
    const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
    const visibleOptions = showOptionsWhenEmpty || search.trim() ? options : options.filter((o) => o.value === value);

    return (
        <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setSearch(""); }}>
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} data-testid={testid} className="w-full justify-between font-normal h-10">
                    <span className={cn("truncate", !selected && "text-slate-400")}>{selected ? selected.label : placeholder}</span>
                    <span className="flex items-center gap-1 ml-2 shrink-0">
                        {allowClear && selected && !disabled && (
                            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onChange(""); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange(""); } }} data-testid={testid ? `${testid}-clear` : undefined} aria-label="Limpiar selección" className="p-0.5 rounded hover:bg-slate-200 cursor-pointer inline-flex items-center">
                                <X className="h-3.5 w-3.5 text-slate-500" />
                            </span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                    </span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
                <Command shouldFilter={!onSearchChange} filter={(itemValue, term) => !term || normalize(itemValue).includes(normalize(term)) ? 1 : 0}>
                    <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} data-testid={testid ? `${testid}-search` : undefined} />
                    <CommandList>
                        {loading && <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Buscando…</div>}
                        {!loading && <CommandEmpty>{!showOptionsWhenEmpty && !search.trim() ? "Escribe para buscar" : emptyText}</CommandEmpty>}
                        {!loading && visibleOptions.length > 0 && (
                            <CommandGroup>
                                {visibleOptions.map((opt) => (
                                    <CommandItem key={opt.value} value={`${opt.label} ${opt.keywords || ""}`} onSelect={() => { onChange(opt.value); setOpen(false); setSearch(""); }} data-testid={testid ? `${testid}-option-${opt.value}` : undefined}>
                                        <Check className={cn("mr-2 h-4 w-4", value === opt.value ? "opacity-100" : "opacity-0")} />
                                        <span className="truncate">{opt.label}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                    {footer && <div className="border-t border-slate-200 p-2">{footer}</div>}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
